import { colors } from '@atlaskit/theme';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useFlaggingCallback } from '@databases/banners';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  selectDatabaseDraftByContentId,
  selectHasActiveSaveOperation,
} from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { DatabaseView, ViewId } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { isUnsavedView } from '@databases/utils/database/view/view';
import { getDefaultView, getViewById } from '@databases/utils/database/view/view';
import { isEmbed } from '@databases/utils/utils';
import { messages as viewsMessages } from '@databases/views/messages';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import React, {
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { EditableListProps, RawEditableList } from '../../EditableList';
import { subtleBorder } from '../../styled';

import ViewCard from './ViewCard';

const getId = (value: DatabaseView): string => value.id ?? value.name;

const Li = styled.li<{
  isActive?: boolean;
}>`
  display: flex;
  align-items: center;
  margin: 8px 8px 0px 8px;
  border-radius: 3px;
  padding-left: 16px;
  background: ${(props) => props.isActive && `${colors.B50}`};
  box-sizing: border-box;

  &:hover {
    background: ${(props) => !props.isActive && `${colors.N20}`};
  }
`;

const Hr = styled.hr`
  border: none;
  border-top: ${subtleBorder};
  width: 388px;
  margin: 7px 4px 0 4px;
`;

export interface ViewListProps {
  contentId: string;
  value: DatabaseView;
  onChange: (newViewId: string) => void;
  canEditViews: boolean;
  defaultView?: DatabaseView;
  handleAddView: () => void;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  handleClose: () => void;
  showViewSettings?: boolean;
  viewPickerRef: RefObject<HTMLDivElement>;
}

const ViewList: FunctionComponent<ViewListProps> = ({
  contentId,
  value,
  onChange,
  canEditViews,
  defaultView,
  handleAddView,
  openViewSettingsPanel,
  handleClose,
  showViewSettings = true,
  viewPickerRef,
}) => {
  const scope = useScope();
  const client = useAnalytics();
  const { unsavedView } = useView();
  const { hidePanel } = usePanel();

  const [duplicateViewNameErrorId, setDuplicateViewNameErrorId] = useState('');

  const intl = useIntl();
  const { formatMessage } = intl;

  const [savedViews, databaseDefaultView, isSaving] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        includeDeleted: true,
      });
      return [
        databaseDraft.views,
        getDefaultView(databaseDraft),
        selectHasActiveSaveOperation(state, { contentId, scope }),
      ] as const;
    }
  );

  const dispatch = defaultStoreUtils.useDispatch();

  const handleReorder = useFlaggingCallback(
    (idx: number, newIdx: number) => {
      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.REORDER_SAVED_VIEW,
          viewId: savedViews[idx].id,
          // EditableList gives the new index _after_ removing the item, while our changeset expects the index _before_ removing the item
          newIndex: newIdx + (newIdx > idx ? 1 : 0),
        })
      );
      client.sendDatabaseViewReorderedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: savedViews[idx].id,
        source: 'databaseViewPicker',
      });
    },
    [client, contentId, dispatch, savedViews]
  );

  const valueIdRef = useRef(value.id);
  valueIdRef.current = value.id;

  const changeViewOnDelete = useFlaggingCallback(() => {
    onChange(defaultView?.id ?? databaseDefaultView.id);
  }, [databaseDefaultView?.id, defaultView?.id, onChange]);

  const handleViewClick = useFlaggingCallback(
    (view: DatabaseView) => {
      client.sendDatabaseViewSelected({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: view.id,
        source: 'databaseViewPicker',
      });
      if (view.id !== value.id) {
        hidePanel({ onClose: () => onChange(view.id) });
      }
    },
    [client, hidePanel, contentId, onChange, value.id]
  );
  useEffect(() => {
    if (!getViewById(savedViews, value.id) && !isUnsavedView(value)) {
      // View was deleted externally
      changeViewOnDelete();
    }
  }, [changeViewOnDelete, savedViews, value]);

  const renderField: EditableListProps<DatabaseView, null>['renderField'] = useCallback(
    ({ value: thisValue }) => {
      const isActive = thisValue.id === value.id;
      const isDefault = isEmbed()
        ? thisValue.id === (defaultView ?? databaseDefaultView).id
        : thisValue.id === databaseDefaultView.id;
      return (
        <ViewCard
          onClick={handleViewClick}
          key={thisValue.id}
          view={isActive ? value : thisValue}
          isActive={isActive}
          isDefault={isDefault}
          isReadonly={!canEditViews}
          contentId={contentId}
          setDuplicateViewNameErrorId={setDuplicateViewNameErrorId}
          hasDuplicateNameError={duplicateViewNameErrorId === thisValue.id}
          existingViewNames={savedViews
            .filter((v) => v.id !== thisValue.id)
            .map((v) => v.name)}
          openViewSettingsPanel={openViewSettingsPanel}
          handleClose={handleClose}
          showViewSettings={showViewSettings}
        />
      );
    },
    [
      value,
      defaultView,
      databaseDefaultView,
      handleViewClick,
      canEditViews,
      contentId,
      duplicateViewNameErrorId,
      savedViews,
      openViewSettingsPanel,
      handleClose,
      showViewSettings,
    ]
  );

  return (
    <div ref={viewPickerRef}>
      {unsavedView && (
        <>
          <Li isActive={value.id === ViewId.UNSAVED}>
            {renderField({
              i: 0,
              value: unsavedView,
              autoFocus: true,
              isReadonly: false,
              isDisabled: false,
              onChange: () => {},
            })}
          </Li>
          {unsavedView && <Hr />}
        </>
      )}
      <RawEditableList<DatabaseView, null>
        getId={getId}
        values={savedViews}
        onAdd={handleAddView}
        onReorder={handleReorder}
        renderField={renderField}
        verticalScroll={true}
        addLabel={formatMessage(viewsMessages['views.add-view-label'])}
        noDelete
        showDivider
        isReadonly={!canEditViews}
        isDisabled={isSaving}
        tooltipContent={formatMessage(messages['toolbar-views.save-new-view-tooltip'])}
        activeId={value.id}
        errorId={duplicateViewNameErrorId}
        showUnsavedView={true}
        isViewsList={true}
      />
    </div>
  );
};

export default ViewList;
